import { useTranslation } from "react-i18next";
import { MessageSquareText } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import type { FeedbackRow } from "@/types/feedback";
import { FeedbackCard } from "./FeedbackCard";
import { FeedbackListSkeleton } from "./FeedbackListSkeleton";

interface Props {
  rows: FeedbackRow[];
  isLoading: boolean;
  /** True when any filter beyond the default date range is active. */
  filtered: boolean;
  onOpenOrder: (orderId: number) => void;
  /**
   * IANA timezone for the submission timestamp (TASK-5043). Supplied by
   * `ReviewsTab` through `FeedbackList`; omitted → the browser zone.
   */
  tenantTz?: string;
}

/**
 * Renders the feedback card list (TASK-3814), handling the loading skeleton and
 * the empty branch (a plain "no feedback yet" message, or a filter-aware
 * message when the empty result is due to active filters).
 */
export function FeedbackList({
  rows,
  isLoading,
  filtered,
  onOpenOrder,
  tenantTz,
}: Props) {
  const { t } = useTranslation();

  if (isLoading) {
    return <FeedbackListSkeleton />;
  }

  if (rows.length === 0) {
    return filtered ? (
      <EmptyState
        icon={MessageSquareText}
        title={t("feedback.emptyFiltered.title")}
        subtitle={t("feedback.emptyFiltered.subtitle")}
      />
    ) : (
      <EmptyState icon={MessageSquareText} title={t("feedback.empty")} />
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <FeedbackCard
          key={row.id}
          row={row}
          onOpenOrder={onOpenOrder}
          tenantTz={tenantTz}
        />
      ))}
    </div>
  );
}
